import React, { useCallback, useEffect, useState } from "react";

import { CalendarRange, Loader2, Save } from "lucide-react";

import AlAssessmentSettingsForm, {
  AlAssessmentSettingsValues,
  allowedWindowDays,
  CADENCE_DAY_OPTIONS,
  DEFAULT_AL_ASSESSMENT_SETTINGS,
  FORMAL_DUE_DATE_OPTIONS,
  parseLevelGapThreshold,
  pickAllowed,
  GRACE_EXTRA_DAY_OPTIONS,
  REMINDER_DAY_OPTIONS,
  RESOLUTION_DAY_OPTIONS,
} from "../../../Components/AlAssessmentSettingsForm";
import Button from "../../../Components/Button";
import { TooltipProvider } from "../../../intelligence-briefing-library/components/ui/tooltip";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchSelfAssessmentConfig,
  SelfAssessmentConfig,
  updateSelfAssessmentConfig,
} from "../../../Store/Reducers/AssessmentSlice";
import {
  DisconnectFlowApiValue,
  fetchDisconnectFlowConfig,
  updateDisconnectFlowConfig,
} from "../../../Store/Reducers/VendorAdminSlice";
import { toast } from "../../../Utils/Helper/toast";

const DISCONNECT_FLOW_KEY = "disconnect_flow";
const D = DEFAULT_AL_ASSESSMENT_SETTINGS;

const fromConfig = (
  config: SelfAssessmentConfig | null | undefined,
  disconnect: DisconnectFlowApiValue | null | undefined,
  current: AlAssessmentSettingsValues
): AlAssessmentSettingsValues => {
  const next = { ...current };
  if (config) {
    next.cadenceDays = pickAllowed(
      config.cadence_days,
      CADENCE_DAY_OPTIONS,
      D.cadenceDays
    );
    next.windowDays = allowedWindowDays(next.cadenceDays, config.window_days);
    next.windowOffsetDays = config.window_offset_days ?? null;
    next.formalReminderDays = pickAllowed(
      config.formal_due_reminder_days,
      REMINDER_DAY_OPTIONS,
      D.formalReminderDays
    );
    next.teamReminderDays = pickAllowed(
      config.team_due_reminder_days,
      REMINDER_DAY_OPTIONS,
      D.teamReminderDays
    );
    next.resolutionDays = pickAllowed(
      config.resolution_days,
      RESOLUTION_DAY_OPTIONS,
      D.resolutionDays
    );
    next.graceExtraDays = pickAllowed(
      config.grace_extra_days,
      GRACE_EXTRA_DAY_OPTIONS,
      D.graceExtraDays
    );
  }
  if (disconnect) {
    next.formalDefaultDueDateDays = pickAllowed(
      disconnect.formal_default_due_date,
      FORMAL_DUE_DATE_OPTIONS,
      D.formalDefaultDueDateDays
    );
    next.disconnectReminderDays = pickAllowed(
      disconnect.reminder,
      REMINDER_DAY_OPTIONS,
      D.disconnectReminderDays
    );
    next.levelGapThreshold = parseLevelGapThreshold(
      disconnect.level_gap_threshold
    );
  }
  return next;
};

const TeamAssessmentOrgSettings: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    selfAssessmentConfig,
    isLoadingSelfAssessmentConfig,
    selfAssessmentConfigError,
    isUpdatingSelfAssessmentConfig,
  } = useAppSelector(state => state.assessment);

  const {
    disconnectFlowValue,
    disconnectFlowSettingsLoading,
    disconnectFlowSettingsError,
    disconnectFlowSettingsUpdateLoading,
  } = useAppSelector(state => state.vendorAdmin);

  const [values, setValues] = useState<AlAssessmentSettingsValues>(D);

  const isLoading =
    isLoadingSelfAssessmentConfig || disconnectFlowSettingsLoading;
  const isSaving =
    isUpdatingSelfAssessmentConfig || disconnectFlowSettingsUpdateLoading;

  useEffect(() => {
    dispatch(fetchSelfAssessmentConfig());
    dispatch(fetchDisconnectFlowConfig());
  }, [dispatch]);

  useEffect(() => {
    setValues(prev =>
      fromConfig(selfAssessmentConfig, disconnectFlowValue, prev)
    );
  }, [selfAssessmentConfig, disconnectFlowValue]);

  useEffect(() => {
    if (selfAssessmentConfigError) {
      toast.error(selfAssessmentConfigError);
    }
  }, [selfAssessmentConfigError]);

  useEffect(() => {
    if (disconnectFlowSettingsError) {
      toast.error(disconnectFlowSettingsError);
    }
  }, [disconnectFlowSettingsError]);

  const handleSave = useCallback(async () => {
    const configId = selfAssessmentConfig?.id;
    if (!configId) {
      toast.error("Team assessment config is not loaded yet.");
      return;
    }

    try {
      await Promise.all([
        dispatch(
          updateSelfAssessmentConfig({
            id: configId,
            formal_due_reminder_days: values.formalReminderDays,
            team_due_reminder_days: values.teamReminderDays,
            cadence_days: values.cadenceDays,
            window_days: allowedWindowDays(
              values.cadenceDays,
              values.windowDays
            ),
            window_offset_days: values.windowOffsetDays,
            resolution_days: values.resolutionDays,
            grace_extra_days: values.graceExtraDays,
          })
        ).unwrap(),
        dispatch(
          updateDisconnectFlowConfig({
            key: DISCONNECT_FLOW_KEY,
            value: {
              ...disconnectFlowValue,
              reminder: values.disconnectReminderDays,
              formal_default_due_date: values.formalDefaultDueDateDays,
              level_gap_threshold: `L${values.levelGapThreshold}`,
            },
          })
        ).unwrap(),
      ]);
      toast.success("AL assessment settings updated.");
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save AL assessment settings."
      );
    }
  }, [dispatch, disconnectFlowValue, selfAssessmentConfig?.id, values]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="settings-section rounded-lg border border-gray-200 bg-white shadow">
        <div className="p-6">
          <div className="mb-1 border-b border-gray-200 pb-4">
            <div className="flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-violet-600" />
              <h2 className="fs-18 fw-600 mb-0 text-gray-900">
                AL Assessment Settings
              </h2>
            </div>
            <p className="mb-0 mt-2 text-sm leading-relaxed text-gray-600">
              Configure the assessment calendar, default due dates, email
              reminders, and disconnect detection for Formal and Team
              assessments.
            </p>
          </div>

          {isLoading ? (
            <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
              Loading…
            </div>
          ) : (
            <>
              <AlAssessmentSettingsForm values={values} onChange={setValues} />

              <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving || !selfAssessmentConfig?.id}
                  loading={isSaving}
                  icon={<Save className="h-4 w-4" />}
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TeamAssessmentOrgSettings;
